import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { HistoricoContextService } from "../../context/historico-context.service";
import { HistoricoService } from "../../services/historico.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-historico-designaciones',
    templateUrl: './historico-designaciones.component.html'
})
export class HistoricoDesignacionesComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public historicoDesignaciones;
    public provincias;
    public municipios;
    public denominaciones;
    public campanyas;
    public etiquetadores;
    public marcas;

    public paisId;

    public required = false;

    public disabledProvincias: Subject<boolean> = new Subject();
    public disabledMunicipios: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();
    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public dropdownInfo: number;
    public dropdownSection: number;
    public pestanaActiva = new Array<number>();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;
    public columnId: number;
    public indexOrder = 11;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _historicoService: HistoricoService,
        private readonly _historicoContext: HistoricoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            provincia: [{ value: '', disabled: true }],
            municipio: [{ value: '', disabled: true }],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            etiquetador: [{ value: '', disabled: true }],
            marca: [{ value: '', disabled: true }],
            desProductor: [''],
            desFinca: ['', Validators.required],
            fechaInicio: [''],
            fechaFin: ['']
        });

        this._historicoContext.historicoDesignacionesFilters = null;

        this.checkWarnings(this.filtersForm.get('campanya').value);

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
                this.getProvinciasByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.disabledProvincias.next(true);
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
                this.filtersForm.get('provincia').patchValue('');
                this.filtersForm.get('provincia').disable();
                this.denominaciones = [];
                this.provincias = [];
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('provincia').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provincia) => {
            if (provincia) {
                this.filtersForm.get('municipio').setValidators(Validators.required);
                this.required = true;
                this.getMunicipiosByProvincia(provincia.id, this.paisId);
            } else {
                this.disabledMunicipios.next(true);
                this.filtersForm.get('municipio').patchValue('');
                this.filtersForm.get('municipio').disable();
                this.filtersForm.get('municipio').clearValidators();
                this.required = false;
                this.municipios = [];
            }
        });

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
                this.campanyas = [];
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.filtersForm.get('desFinca').clearValidators();
                this.filtersForm.get('desFinca').updateValueAndValidity({ onlySelf: true, emitEvent: false });
                this.getEtiquetadoresByCampanya(campanya.id, this.paisId);
            } else {
                this.filtersForm.get('desFinca').setValidators(Validators.required);
                this.filtersForm.get('desFinca').updateValueAndValidity({ onlySelf: true, emitEvent: false });
                this.disabledEtiquetadores.next(true);
                this.filtersForm.get('etiquetador').patchValue('');
                this.filtersForm.get('etiquetador').disable();
                this.etiquetadores = [];
            }
        });

        this.filtersForm.get('desFinca').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((des) => {
            if (des) {
                this.filtersForm.get('campanya').clearValidators();
                this.filtersForm.get('campanya').updateValueAndValidity({ onlySelf: true, emitEvent: false });
            } else {
                this.filtersForm.get('campanya').setValidators(Validators.required);
                this.filtersForm.get('campanya').updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        });

        this.filtersForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getMarcasByEtiquetador(etiquetador.id, this.paisId);
            } else {
                this.disabledMarcas.next(true);
                this.filtersForm.get('marca').patchValue('');
                this.filtersForm.get('marca').disable();
                this.marcas = [];
            }
        });

        this.indexOrder = this.role === 'Administrador' || this.claims.includes('D_HDS_AforoVisible') ? this.indexOrder : 10;

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get claims() {
        return this._sessionContext.sessionUser.lsClaimTypes;
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: true
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                if (this.campanyas.length === 1) {
                    this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
                } else {
                    this.filtersForm.get('campanya').enable();
                    this.disabledCampanyas.next(false);
                }
            }
        });
    }

    public getProvinciasByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getProvincias(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.provincias = resp.lista;
                this.filtersForm.get('provincia').enable();
                this.disabledProvincias.next(false);
            }
        });
    }

    public getMunicipiosByProvincia(provinciaId, paisId) {
        let filters = {
            paisId: paisId,
            provinciaId: provinciaId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getMunicipios(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.municipios = resp.lista;
                this.filtersForm.get('municipio').enable();
                this.disabledMunicipios.next(false);
            }
        });
    }

    public getEtiquetadoresByCampanya(campanyaId, paisId) {
        let filters = {
            paisId: paisId,
            campanyaId: campanyaId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getEtiquetadores(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.etiquetadores = resp.lista;
                if (this.etiquetadores.length === 1) {
                    this.filtersForm.get('etiquetador').patchValue(this.etiquetadores[0]);
                    this.filtersForm.get('etiquetador').disable();
                    this.disabledEtiquetadores.next(true);
                } else {
                    this.filtersForm.get('etiquetador').enable();
                    this.disabledEtiquetadores.next(false);
                }

            }
        });
    }

    public getMarcasByEtiquetador(etiquetadorId, paisId) {
        let filters = {
            paisId: paisId,
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getMarcas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            }
        });
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public pageChange(page) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this._historicoContext.historicoDesignacionesFilters.numPagina = this.currentPage = page - 1;
        this.getHistoricoDesignaciones();
    }

    public itemsPerPageChange(items) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this._historicoContext.historicoDesignacionesFilters.numPagina = this.currentPage = 0;
        this._historicoContext.historicoDesignacionesFilters.tamPagina = this.itemsPerPage = items;
        this.getHistoricoDesignaciones();
    }

    private checkWarnings(campanya) {
        if (campanya && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getHistoricoDesignaciones();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.campanyaOFinca' });
        }
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('provincia').value ||
            this.filtersForm.get('municipio').value ||
            this.filtersForm.get('etiquetador').value ||
            this.filtersForm.get('marca').value ||
            this.filtersForm.get('desProductor').value ||
            this.filtersForm.get('desFinca').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
      if(this.filtersForm.get('pais').disabled) {
        this.filtersForm.reset({
          pais: this.filtersForm.get('pais').value
        });
      } else {
        this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['desFinca'].reset();
        this.filtersForm.controls['desProductor'].reset();
        this.filtersForm.controls['fechaInicio'].reset();
        this.filtersForm.controls['fechaFin'].reset();
      }
    }

    public showDetails(i: number) {
        this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
        if (this.dropdownInfo !== -1 && window.innerWidth <= 1199) {
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public showSection(i) {
        this.dropdownSection = this.dropdownSection === i ? -1 : i;
        this.pestanaActiva[i] = this.pestanaActiva[i] > 0 ? this.pestanaActiva[i] : 1;
        // Desplazamiento a la izquierda para tablet (mismo tamaño máx. que en table.scss)
        if (this.dropdownSection !== -1 && window.innerWidth <= 1199) {
            // Retardo en caso de que haya otro detalle abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public filtrar() {
        this.validDates();

        if (!this.filtersForm.get('campanya').value && !this.filtersForm.get('desFinca').value) {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.info.campanyaOFinca' });
        }

        if (Object.keys(this.filtersForm.controls)
            .filter(key => key !== 'campanya' && key !== 'desFinca')
            .some(key => this.filtersForm.get(key).hasError('required'))) {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }

        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._historicoContext.historicoDesignacionesFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : null,
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : null,
            marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : null,
            municipioId: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : null,
            desProductor: this.filtersForm.get('desProductor').value ? this.filtersForm.get('desProductor').value : '',
            desFinca: this.filtersForm.get('desFinca').value ? this.filtersForm.get('desFinca').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getHistoricoDesignaciones();
    }

    public getHistoricoDesignaciones() {
        if (this._historicoContext.historicoDesignacionesFilters) {
            this._filters = this._historicoContext.historicoDesignacionesFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._historicoService.getHistoricoDesignaciones(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.historicoDesignaciones = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public exportarPdfOrExcel(tipo) {
        if (this.historicoDesignaciones) {
            if (this._historicoContext.historicoDesignacionesFilters) {
                var mimeType;
                this._filters = this._historicoContext.historicoDesignacionesFilters;
                this._filters.tamPagina = 0;
                if (tipo === 'excel') {
                    this._filters.tipoArchivo = 'Excel';
                    mimeType = 'data:application/vnd.ms-excel;base64,';
                } else {
                    this._filters.tipoArchivo = 'Pdf';
                    mimeType = 'data:application/pdf;base64,';
                }
            }
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._historicoService.getRepHistoricoDesignacion(this._filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                const downloadLink = document.createElement('a');
                downloadLink.href = mimeType + resp.base64Archivo;
                downloadLink.download = resp.nombreArchivo;
                downloadLink.click();
            }, (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.file' });
            });
        } else {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public getRolePermission() {
        let disabled = false;

        if (this.role === 'Administrador') {
            disabled = false;
        } else if (this.role === 'Auditor') {
            disabled = false;
        } else if (this.role === 'Supervisor') {
            disabled = false;
        }

        return disabled;
    }

    public getIcon(prop: string) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        this.columnId = idColumn;
        if (this.historicoDesignaciones && this.historicoDesignaciones.length > 0) {
            if (this._historicoContext.historicoDesignacionesFilters) {
                this._filters = this._historicoContext.historicoDesignacionesFilters;
                if (!this.orderBy || this.orderBy.includes('DESC')) {
                    this.orderBy = 'ASC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: false
                        }
                    ];
                } else if (this.orderBy.includes('ASC')) {
                    this.orderBy = 'DESC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: true
                        }
                    ];
                }
                this.getHistoricoDesignaciones();
            }
        }
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._historicoService.getDocHelp('History_Designations.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

    public trackByDesignacionId(index: number, historico: { designacionId: number }): number {
      return historico.designacionId;
    }
}
